import { Roles } from "../constants/roles";

import { FormBase } from "../util/form-base-form";

export class CommentForm extends FormBase {
    public getEventTarget(event: Event): Element | null {
        return this.fromEventTarget(event, Element);
    }

    public getElementModalBody(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement);
    }

    public isElementModalOpen(): boolean {
        const modal = this.queryElement(document, `[data-role="${Roles.ELEMENT_MODAL}"]`, HTMLElement);
        return !!modal && modal.offsetParent !== null;
    }

    public getSearchRoot(contextElement: Element | null = null): ParentNode {
        if (contextElement instanceof Element) {
            const modalBody = this.closest(contextElement, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement);
            if (modalBody) {
                return modalBody;
            }
        }

        if (this.isElementModalOpen()) {
            return this.getElementModalBody(document) || document;
        }

        return document;
    }

    public getCommentsSection(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENTS_SECTION}"]`, HTMLElement);
    }

    public getClosestCommentsSection(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.COMMENTS_SECTION}"]`, HTMLElement);
    }

    public getClosestCommentsSectionClass(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, ".comments-section", HTMLElement);
    }

    public getCommentsList(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENTS_LIST}"]`, HTMLElement);
    }

    public getCommentEmpty(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_EMPTY}"]`, HTMLElement);
    }

    public getCommentEmptyYet(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_EMPTY_YET}"]`, HTMLElement);
    }

    public getCommentForm(root: ParentNode = document): HTMLFormElement | null {
        return this.getForm(root, `form[data-role="${Roles.COMMENT_FORM}"][data-board-id][data-post-id]`);
    }

    public isCommentSubmitForm(form: Element | null): form is HTMLFormElement {
        return (
            form instanceof HTMLFormElement &&
            (form.matches(`[data-role="${Roles.COMMENT_FORM}"]`) ||
                form.matches(`[data-role="${Roles.COMMENT_REPLY_FORM_INNER}"]`) ||
                form.matches(`[data-role="${Roles.COMMENT_EDIT_FORM_INNER}"]`))
        );
    }

    public isEditInnerForm(form: Element | null): form is HTMLFormElement {
        return form instanceof HTMLFormElement && form.matches(`[data-role="${Roles.COMMENT_EDIT_FORM_INNER}"]`);
    }

    public isReplyInnerForm(form: Element | null): form is HTMLFormElement {
        return form instanceof HTMLFormElement && form.matches(`[data-role="${Roles.COMMENT_REPLY_FORM_INNER}"]`);
    }

    public getCommentItem(root: ParentNode, commentId: number): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.COMMENT_ITEM}"][data-comment-id="${String(commentId)}"]`,
            HTMLElement,
        );
    }

    public getCommentItems(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.COMMENT_ITEM}"]`, HTMLElement);
    }

    public getTempCommentItems(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.COMMENT_ITEM}"][data-comment-id^="temp_"]`, HTMLElement);
    }

    public getCommentText(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_TEXT}"]`, HTMLElement);
    }

    public getCommentContentTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.getTextarea(root, 'textarea[name="content"]');
    }

    public getParentIdInput(root: ParentNode): HTMLInputElement | null {
        return this.getInput(root, 'input[name="parent_id"]');
    }

    public getSubmitButton(root: ParentNode): HTMLButtonElement | null {
        return this.getButton(root, 'button[type="submit"]');
    }

    public getEditFormWrapper(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_EDIT_FORM}"]`, HTMLElement);
    }

    public getEditForm(root: ParentNode): HTMLFormElement | null {
        return this.getForm(root, `form[data-role="${Roles.COMMENT_EDIT_FORM_INNER}"]`);
    }

    public getReplyForm(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_REPLY_FORM}"]`, HTMLElement);
    }

    public getReplyForms(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.COMMENT_REPLY_FORM}"]`, HTMLElement);
    }

    public getReplyInnerForm(root: ParentNode): HTMLFormElement | null {
        return this.getForm(root, `form[data-role="${Roles.COMMENT_REPLY_FORM_INNER}"]`);
    }

    public getRepliesContainer(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_REPLIES}"]`, HTMLElement);
    }

    public getRepliesLoading(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_REPLIES_LOADING}"]`, HTMLElement);
    }

    public getReplyToggleButton(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.COMMENT_REPLY_TOGGLE}"]`, HTMLElement);
    }

    public getClosestReplyToggleButton(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.COMMENT_REPLY_TOGGLE}"]`, HTMLElement);
    }

    public getClosestCommentMenuTrigger(element: Element | Event | null): HTMLElement | null {
        return this.closest(
            element,
            `[data-role="${Roles.COMMENT_MENU}"], [data-role="${Roles.COMMENT_MENU_BUTTON}"]`,
            HTMLElement,
        );
    }

    public getCommentTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.getTextarea(root, `textarea[data-role="${Roles.COMMENT_TEXTAREA}"]`);
    }

    public getEditTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.getTextarea(root, `textarea[data-role="${Roles.COMMENT_EDIT_TEXTAREA}"]`);
    }

    public getReplyTextarea(root: ParentNode): HTMLTextAreaElement | null {
        return this.getTextarea(root, `textarea[data-role="${Roles.COMMENT_REPLY_TEXTAREA}"]`);
    }

    public getCommentLikeButton(root: ParentNode, commentId: number): HTMLElement | null {
        return this.queryElement(
            root,
            `[data-role="${Roles.LIKE_BUTTON}"][data-like-scope="comment"][data-comment-id="${String(commentId)}"]`,
            HTMLElement,
        );
    }

    public getPendingTimestamps(root: ParentNode): HTMLElement[] {
        return this.queryElements(
            root,
            `[data-role="${Roles.COMMENT_TIMESTAMP}"][data-pending-display="1"]`,
            HTMLElement,
        );
    }

    public getClosestCommentEditAction(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.COMMENT_ACTION_EDIT}"]`, HTMLElement);
    }

    public getClosestCommentDeleteAction(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.COMMENT_ACTION_DELETE}"]`, HTMLElement);
    }

    public getClosestEditCancel(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.COMMENT_EDIT_CANCEL}"]`, HTMLElement);
    }

    public getClosestEditInnerForm(element: Element | null): HTMLFormElement | null {
        return this.closest(element, `form[data-role="${Roles.COMMENT_EDIT_FORM_INNER}"]`, HTMLFormElement);
    }

    public getAnyLikeButton(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, `[data-role="${Roles.LIKE_BUTTON}"][data-board-id][data-post-id]`, HTMLElement);
    }

    public getClosestElementModalBody(element: Element | null): HTMLElement | null {
        if (!(element instanceof Element)) return null;
        return this.closest(element, `[data-role="${Roles.ELEMENT_MODAL_BODY}"]`, HTMLElement);
    }

    public getCommentCountActionElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.COMMENT_COUNT_ACTION}"]`, HTMLElement);
    }

    public getCommentCountHeaderElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, `[data-role="${Roles.COMMENT_COUNT_HEADER}"]`, HTMLElement);
    }

    public getAllMentionTargetTextareas(root: ParentNode = document): HTMLTextAreaElement[] {
        const roles = [Roles.COMMENT_TEXTAREA, Roles.COMMENT_REPLY_TEXTAREA, Roles.COMMENT_EDIT_TEXTAREA];
        return roles.flatMap((role) =>
            this.queryElements(root, `textarea[data-role="${role}"]`, HTMLTextAreaElement),
        );
    }
}

export const commentForm = new CommentForm();
